import { FC, useMemo } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { useNavigate, useParams } from "react-router-dom";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { Loading } from "@hostingbv/lib/src/components/Loading";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { Card } from "@hostingbv/lib/src/components/Card";
import { faKey, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Copyable } from "@hostingbv/lib/src/components/content/Copyable";
import { SshKey } from "@hostingbv/lib/src/api/partner/models/SshKey";
import { DataTable, navigateRow } from "@ventumsoftware/react-toolkit";
import { Entity } from "@hostingbv/lib/src/api/partner/models/Entity";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { SectionDivider } from "@hostingbv/lib/src/components/content/SectionDivider";
import { useModal } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { DeleteSshKeyModal } from "../modals/DeleteSshKeyModal";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";

export const SshKeyDetails: FC = () => {
  const { sshKey: uuid } = useParams();
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { resource, reload } = useFetchedResource(() => partnerClient.getSshKey(uuid ?? ""));
  const sshKey: SshKey | null = useMemo(() => (resource?.success === true ? resource.data : null), [resource]);
  useRefreshEffect(() => {
    reload();
  });
  const navigate = useNavigate();

  const { open: openDeleteModal } = useModal({
    title: "Revoke all access",
    body: <DeleteSshKeyModal publicKey={sshKey} />,
  });

  const displayType = (type: string) => {
    switch (type) {
      case "shared_hosting_subscription":
        return "Site";
      case "shared_hosting_server":
        return "Shared Hosting Server";
      case "domain":
        return "Domain";
      default:
        return type;
    }
  };
  const entityPath = (entity: Entity): string | undefined => {
    switch (entity.type) {
      case "shared_hosting_subscription":
        return `/sites/${entity.id}`;
      case "shared_hosting_server":
        return `/servers/${entity.id}`;
      case "domain":
        return `/domains/${entity.id}`;
    }
  };

  return (
    <ContentContainer>
      <PageIconHeader icon={faKey}>
        <Breadcrumbs
          currentPage={sshKey?.displayName ?? "SSH Key"}
          crumbs={[{ label: "SSH Keys", href: "/ssh-keys" }]}
        />
        <PageHeader>
          {sshKey === null ? (
            <span className={"block h-8 w-36 rounded-lg bg-slate-200 animate-pulse"}></span>
          ) : (
            sshKey.displayName
          )}
        </PageHeader>
      </PageIconHeader>

      <Loading loading={sshKey === null}>
        {sshKey && (
          <Card title={"Details"}>
            <div className={"flex"}>
              <dl className={"flex-1"}>
                <dt className={"text-slate-500 text-xs"}>Type</dt>
                <dd className={"text-slate-900 mb-4"}>{sshKey.type === "personal" ? "Personal" : "Deployment"}</dd>
                <dt className={"text-slate-500 text-xs"}>{sshKey.type === "personal" ? "Owner" : "Name"}</dt>
                <dd className={"text-slate-900 mb-4"}>{sshKey.displayName}</dd>
                <dt className={"text-slate-500 text-xs"}>Fingerprint</dt>
                <dd className={"text-slate-900 mb-4"}>
                  <Copyable text={sshKey.fingerprint} />
                </dd>
                <dt className={"text-slate-500 text-xs"}>Created</dt>
                <dd className={"text-slate-900 mb-4"}>{sshKey.created.toLocaleDateString("nl")}</dd>
              </dl>
              {can("delete", "authorizedKeys") && (
                <div>
                  <Button
                    type={"danger"}
                    size={"sm"}
                    text={"Revoke all access"}
                    icon={faTrash}
                    onClick={openDeleteModal}
                  />
                </div>
              )}
            </div>
          </Card>
        )}
        {sshKey?.entities && (
          <section>
            <SectionDivider />
            <SectionHeader>Access</SectionHeader>
            <Paragraph>Access to the following {sshKey.uses} sites and servers is granted to this key:</Paragraph>
            <DataTable<Entity>
              data={sshKey.entities}
              keyProperty={"id"}
              columns={[
                { property: "displayName", header: "Name" },
                { property: "type", header: "Type", transform: displayType },
                { property: "customerName", header: "Customer" },
              ]}
              onClickRow={(entity, e) => {
                const path = entityPath(entity);
                if (path) navigateRow(navigate, path, e);
              }}
            />
          </section>
        )}
      </Loading>
    </ContentContainer>
  );
};
